import { useState, useEffect, useCallback, useRef } from 'react';
import { getSites, getStores, createStore } from '../api/client';

interface Site {
  _id: string;
  name: string;
  category: string;
}

interface Store {
  _id: string;
  siteId: string;
  name: string;
  address?: string;
  storeType: string;
  businessStatus: string;
  isVerified: boolean;
}

function AddStoreModal({
  sites,
  onClose,
  onCreated,
}: {
  sites: Site[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    siteId: sites[0]?._id || '',
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    storeType: 'pin_store',
    businessStatus: 'OPERATIONAL',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.siteId || !formData.name.trim() || !formData.latitude || !formData.longitude) {
      setFormError('Site, name, latitude, and longitude are required');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      await createStore({
        siteId: formData.siteId,
        name: formData.name.trim(),
        address: formData.address.trim() || undefined,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        storeType: formData.storeType,
        businessStatus: formData.businessStatus,
      });
      onCreated();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create store');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Add Store</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {formError && (
            <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm">{formError}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Site *</label>
            <select
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={formData.siteId}
              onChange={(e) => setFormData(prev => ({ ...prev, siteId: e.target.value }))}
            >
              {sites.map((site) => (
                <option key={site._id} value={site._id}>{site.name} ({site.category})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input
              type="text"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Frontier Trading Post"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
            <input
              type="text"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Optional address"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Latitude *</label>
              <input
                type="number"
                step="any"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="28.4177"
                value={formData.latitude}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Longitude *</label>
              <input
                type="number"
                step="any"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="-81.5812"
                value={formData.longitude}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Store Type</label>
              <select
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={formData.storeType}
                onChange={(e) => setFormData(prev => ({ ...prev, storeType: e.target.value }))}
              >
                <option value="pin_store">Pin Store</option>
                <option value="gift_shop">Gift Shop</option>
                <option value="merchandise">Merchandise</option>
                <option value="cart">Cart</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={formData.businessStatus}
                onChange={(e) => setFormData(prev => ({ ...prev, businessStatus: e.target.value }))}
              >
                <option value="OPERATIONAL">Operational</option>
                <option value="CLOSED_TEMPORARILY">Closed Temporarily</option>
                <option value="CLOSED_PERMANENTLY">Closed Permanently</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors">
              {saving ? 'Creating...' : 'Create Store'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Stores() {
  const [sites, setSites] = useState<Site[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showAddStore, setShowAddStore] = useState(false);
  const limit = 50;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    async function fetchSites() {
      try {
        const result = await getSites();
        setSites(result.data?.sites as Site[] || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sites');
      }
    }
    fetchSites();
  }, []);

  // Reset when site filter changes
  useEffect(() => {
    setStores([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
  }, [selectedSite]);

  const fetchStores = useCallback(async (pageNum: number, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await getStores(selectedSite || undefined, pageNum, limit);
      const newStores = result.data?.stores as Store[] || [];
      const totalCount = result.data?.pagination?.total || newStores.length;

      if (isLoadMore) {
        setStores(prev => [...prev, ...newStores]);
      } else {
        setStores(newStores);
      }

      setTotal(totalCount);
      setHasMore(pageNum * limit < totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stores');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedSite]);

  // Initial load
  useEffect(() => {
    fetchStores(1);
  }, [fetchStores]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchStores(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, page, fetchStores]);

  const getSiteName = (siteId: string) => {
    const site = sites.find((s) => s._id === siteId);
    return site?.name || 'Unknown';
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'OPERATIONAL':
        return 'bg-green-100 text-green-700';
      case 'CLOSED_TEMPORARILY':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-red-100 text-red-700';
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">Stores</h2>
          <div className="text-gray-500 text-sm sm:text-base">
            {total > 0 ? `${total.toLocaleString()} total` : `${stores.length} stores`} across {sites.length} sites
            {stores.length > 0 && stores.length < total && ` / ${stores.length} shown`}
          </div>
        </div>
        <div className="w-full sm:w-auto">
          <button
            onClick={() => setShowAddStore(true)}
            className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
          >
            Add Store
          </button>
        </div>
      </div>

      {/* Site Filter */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <label className="font-medium text-slate-800 text-sm sm:text-base">Filter by Site:</label>
          <select
            className="w-full sm:w-80 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
          >
            <option value="">All Sites</option>
            {sites.map((site) => (
              <option key={site._id} value={site._id}>
                {site.name} ({site.category})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12 text-gray-500">
          Loading stores...
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3 mb-6">
            {stores.map((store) => (
              <div key={store._id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-slate-800">{store.name}</div>
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full ${getStatusBadgeClasses(store.businessStatus)}`}>
                    {store.businessStatus === 'OPERATIONAL' ? 'Open' : 'Closed'}
                  </span>
                </div>
                <div className="text-sm text-gray-500 mb-1">{getSiteName(store.siteId)}</div>
                {store.address && <div className="text-xs text-gray-400">{store.address}</div>}
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600">
                    {store.storeType}
                  </span>
                  {store.isVerified && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-700">
                      Verified
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-700 text-white">
                    <th className="px-4 lg:px-5 py-3 lg:py-4 text-left text-xs font-bold uppercase tracking-wider">Name</th>
                    <th className="px-4 lg:px-5 py-3 lg:py-4 text-left text-xs font-bold uppercase tracking-wider">Site</th>
                    <th className="px-4 lg:px-5 py-3 lg:py-4 text-left text-xs font-bold uppercase tracking-wider hidden lg:table-cell">Type</th>
                    <th className="px-4 lg:px-5 py-3 lg:py-4 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                    <th className="px-4 lg:px-5 py-3 lg:py-4 text-left text-xs font-bold uppercase tracking-wider hidden lg:table-cell">Verified</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stores.map((store) => (
                    <tr key={store._id} className="hover:bg-gray-50">
                      <td className="px-4 lg:px-5 py-3 lg:py-4">
                        <div>
                          <div className="font-medium text-slate-800">{store.name}</div>
                          {store.address && <div className="text-xs text-gray-500 mt-0.5 hidden lg:block">{store.address}</div>}
                        </div>
                      </td>
                      <td className="px-4 lg:px-5 py-3 lg:py-4 text-gray-600">{getSiteName(store.siteId)}</td>
                      <td className="px-4 lg:px-5 py-3 lg:py-4 hidden lg:table-cell">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600">
                          {store.storeType}
                        </span>
                      </td>
                      <td className="px-4 lg:px-5 py-3 lg:py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full ${getStatusBadgeClasses(store.businessStatus)}`}>
                          {store.businessStatus === 'OPERATIONAL' ? 'Open' : store.businessStatus}
                        </span>
                      </td>
                      <td className="px-4 lg:px-5 py-3 lg:py-4 hidden lg:table-cell">
                        {store.isVerified ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-700">Yes</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-600">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Load More Trigger */}
            <div ref={loadMoreRef} className="h-10 flex items-center justify-center border-t border-gray-200">
              {loadingMore && <div className="text-gray-500 text-xs">Loading more stores...</div>}
              {!hasMore && stores.length > 0 && <div className="text-gray-400 text-xs">End of list</div>}
            </div>
          </div>
        </>
      )}

      {/* Sites Summary */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
          <span className="font-bold text-sm sm:text-base">&#x1F3F0; Sites Summary</span>
        </div>
        <div className="p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          {sites.map((site) => {
            const siteStores = stores.filter((s) => s.siteId === site._id);
            return (
              <div
                key={site._id}
                className="p-2.5 sm:p-3 bg-gray-50 rounded-md flex justify-between items-center hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={() => setSelectedSite(site._id)}
              >
                <span className="text-slate-800 text-xs sm:text-sm font-medium truncate">{site.name}</span>
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-200 text-gray-600 ml-2 flex-shrink-0">
                  {siteStores.length}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {showAddStore && (
        <AddStoreModal
          sites={sites}
          onClose={() => setShowAddStore(false)}
          onCreated={() => {
            setShowAddStore(false);
            setStores([]);
            setPage(1);
            setHasMore(true);
            fetchStores(1);
          }}
        />
      )}
    </div>
  );
}
